<div class="location-filter-container relative">
  <!-- Trigger Button -->
  <button
    type="button"
    (click)="toggleDropdown()"
    class="flex items-center justify-between px-4 py-2 max-w-[230px] sm:max-w-[720px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200"
    [class.ring-2]="isOpen()"
    [class.ring-primary-500]="isOpen()"
  >
    <!-- Left: Location Icon -->
    <i
      class="fas fa-map-marker-alt text-primary-600 text-base flex-shrink-0"
    ></i>

    <!-- Center: Text with clear button -->
    <div class="flex items-center gap-2 flex-1 mx-3 min-w-0">
      <span class="hidden sm:inline truncate text-sm">
        {{ currentLocation() ? displayText() : ('location.selectLocation' | translate) }}
      </span>
      <span class="sm:hidden truncate text-sm">{{
        currentLocation() ? displayText() : ('location.location' | translate)
      }}</span>

      <!-- Clear button (only show when location is selected) -->
      @if (currentLocation()) {
      <i
        class="fas fa-times text-gray-400 hover:text-gray-600 flex-shrink-0"
        (click)="clearLocation($event)"
      ></i>
      }
    </div>

    <!-- Right: Dropdown arrow -->
    <i
      class="fas fa-chevron-down text-gray-400 transition-transform duration-200 flex-shrink-0"
      [class.rotate-180]="isOpen()"
    ></i>
  </button>

  <!-- Dropdown Menu -->
  @if (isOpen()) {
  <div
    class="absolute left-1/2 -translate-x-1/2 mt-2 w-full min-w-[320px] max-w-md bg-white rounded-lg shadow-2xl border border-gray-200 z-[100] overflow-hidden animate-slideDown"
  >
    <!-- Search Input -->
    <div class="p-3 border-b border-gray-200">
      <div class="relative mb-2">
        <i
          class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        ></i>
        <input
          #searchInput
          type="text"
          [value]="searchQuery()"
          (input)="onSearchInput($event)"
          (keydown)="onKeydown($event)"
          [placeholder]="'location.searchLocation' | translate"
          class="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <!-- Use Current Location Button -->
      <button
        type="button"
        (click)="useCurrentLocation()"
        [disabled]="isGettingGeolocation()"
        class="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        @if (isGettingGeolocation()) {
          <i class="fas fa-spinner fa-spin"></i>
          <span>{{ 'location.gettingLocation' | translate }}</span>
        } @else {
          <i class="fas fa-location-crosshairs"></i>
          <span>{{ 'location.useMyLocation' | translate }}</span>
        }
      </button>
    </div>

    <!-- Location List -->
    <div class="max-h-64 overflow-y-auto">
      <!-- Loading State -->
      @if (isLoading()) {
      <div
        class="px-4 py-8 text-center text-gray-500 text-sm"
      >
        <i class="fas fa-spinner fa-spin text-2xl mb-2 opacity-50"></i>
        <p>{{ 'location.searchingLocations' | translate }}</p>
      </div>
      }

      <!-- Error State -->
      @else if (error()) {
      <div class="px-4 py-4 text-center text-sm">
        <i class="fas fa-exclamation-circle text-xl mb-2 text-red-500"></i>
        <p class="text-red-600 mb-3">{{ error() }}</p>
        <button
          type="button"
          (click)="useCurrentLocation()"
          class="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 font-medium rounded-lg transition-colors duration-200 text-sm"
        >
          <i class="fas fa-redo mr-2"></i>
          {{ 'common.retry' | translate }}
        </button>
      </div>
      }

      <!-- Results -->
      @else if (filteredLocations().length > 0) { @for (location of
      filteredLocations(); track location.placeId) {
      <button
        type="button"
        (click)="selectLocation(location)"
        class="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 flex items-start gap-3"
        [ngClass]="{
          'bg-primary-50':
            currentLocation()?.placeId === location.placeId
        }"
      >
        <i
          class="fas fa-map-marker-alt text-primary-600 mt-1"
        ></i>
        <div class="flex-1 min-w-0">
          <div class="font-medium text-gray-900 truncate">
            {{ location.mainText }}
          </div>
          <div class="text-sm text-gray-500 truncate">
            {{ location.secondaryText }}
          </div>
        </div>
        @if (currentLocation()?.placeId === location.placeId) {
        <i class="fas fa-check text-primary-600 mt-1"></i>
        }
      </button>
      } }

      <!-- Empty State -->
      @else if (!isLoading() && searchQuery().length >= 2) {
      <div
        class="px-4 py-8 text-center text-gray-500 text-sm"
      >
        <i class="fas fa-search text-2xl mb-2 opacity-50"></i>
        <p>{{ 'location.noLocationsFound' | translate }}</p>
        <p class="text-xs mt-1">{{ 'location.tryAnotherSearch' | translate }}</p>
      </div>
      }
    </div>

    <!-- Footer with current location info -->
    @if (currentLocation()) {
    <div
      class="p-3 bg-gray-50 border-t border-gray-200"
    >
      <div
        class="flex items-center justify-between text-xs text-gray-600"
      >
        <span>
          <i class="fas fa-check-circle text-green-500 mr-1"></i>
          {{ 'location.selectedLocation' | translate }}
        </span>
        <button
          type="button"
          (click)="clearLocation($event)"
          class="text-primary-600 hover:underline"
        >
          {{ 'location.clear' | translate }}
        </button>
      </div>
    </div>
    }
  </div>
  }
</div>
